import { useState } from "react";
import { useNavigate } from "react-router";
import BackLink from "../../../components/atoms/BackLink";
import Loader from "../../../components/atoms/Loader";
import AiAssistantTemplateForm, {
  AiAssistantTemplateFormType,
} from "../../../components/features/AiAssistantTemplateForm";
import type { AiAssistantTemplateFormValues } from "../../../components/features/AiAssistantTemplateForm/validationSchema";
import { UnprocessableEntityError } from "../../../errors";
import { useQueryError } from "../../../hooks/http/useQueryError";
import { useFlashMessage } from "../../../hooks/useFlashMessage";
import { ApiErrorInterface } from "../../../interfaces/ApiErrorResponseInterface";
import SectionInterface from "../../../interfaces/SectionInterface";
import { useDeleteAiAssistantTemplate } from "./useDeleteAiAssistantTemplate";
import { useFetchAiAssistantTemplate } from "./useFetchAiAssistantTemplate";
import { useUpdateAiAssistantTemplate } from "./useUpdateAiAssistantTemplate";

interface Props {
  section: SectionInterface;
  templateId: string;
}

const SectionsSettingsAiAssistantTemplatesEdit = ({
  section,
  templateId,
}: Props) => {
  const [apiErrors, setApiErrors] = useState<ApiErrorInterface[]>([]);
  const navigate = useNavigate();
  const { showSuccessMessage, showErrorMessage } = useFlashMessage();

  const { data, isPending, error } = useFetchAiAssistantTemplate({
    sectionId: section.id,
    templateId,
  });
  useQueryError(error);

  const updateMutation = useUpdateAiAssistantTemplate({
    sectionId: section.id,
    templateId,
  });

  const deleteMutation = useDeleteAiAssistantTemplate({
    sectionId: section.id,
    templateId,
  });

  const handleUpdateTemplate = (values: AiAssistantTemplateFormValues) => {
    updateMutation.mutate(values, {
      onSuccess: () => {
        showSuccessMessage("テンプレートを更新しました");
        navigate(`/sections/${section.id}/settings/ai_assistant_templates`);
      },
      onError: (error) => {
        showErrorMessage("テンプレートを更新できませんでした");
        if (error instanceof UnprocessableEntityError) {
          setApiErrors(error.originalErrors);
        }
      },
    });
  };

  const handleDeleteTemplate = () => {
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        showSuccessMessage("テンプレートを削除しました");
        navigate(`/sections/${section.id}/settings/ai_assistant_templates`);
      },
      onError: () => {
        showErrorMessage("テンプレートを削除できませんでした");
      },
    });
  };

  return (
    <>
      <BackLink>戻る</BackLink>
      {data && (
        <AiAssistantTemplateForm
          type={AiAssistantTemplateFormType.Edit}
          aiAssistantTemplate={data}
          submitting={updateMutation.isPending}
          loading={isPending}
          onSubmit={handleUpdateTemplate}
          onDelete={handleDeleteTemplate}
          apiErrors={apiErrors}
        />
      )}
      <Loader loading={isPending} />
    </>
  );
};

export default SectionsSettingsAiAssistantTemplatesEdit;
